import { ApolloClient } from '@apollo/client'
import { FormInstance, message } from 'antd'

import { EDM_CALCULATOR_FORM_KEYS, EDM_TYPES } from './constants'
import { edmCalculatorPendingPriceQuery } from './edmCalculatorPendingPriceQuery'
import {
    edmCalculatorPendingPriceQuery as EdmCalculatorPendingPriceQueryType,
    edmCalculatorPendingPriceQueryVariables as edmCalculatorPendingPriceQueryVariablesType,
} from './__generated__/edmCalculatorPendingPriceQuery'
import { StepItemType } from './types'

const generateKey = (index: number, suffix: string) => {
    return `${index}${suffix}`
}

export const generateStepItems = (
    form: FormInstance,
    steps: number[],
): { stepItems: StepItemType[] | null; reason?: string } => {
    const stepItemsFormValues: StepItemType[] = []

    for (let i = 0; i < steps.length; i++) {
        const index = steps[i]
        const edmTypeKey = generateKey(
            index,
            EDM_CALCULATOR_FORM_KEYS.EDM_TYPE_SUFFIX,
        )
        const { [edmTypeKey]: edmType } = form.getFieldsValue([edmTypeKey])

        const stepItem: StepItemType = {
            wire: null,
            sinkerKnownRemoval: null,
            sinkerNoKnownRemoval: null,
        }

        if (edmType === EDM_TYPES.WIRE) {
            const gearKey = generateKey(
                index,
                EDM_CALCULATOR_FORM_KEYS.GEAR_SUFFIX,
            )
            const roughnessKey = generateKey(
                index,
                EDM_CALCULATOR_FORM_KEYS.ROUGHNESS_CATEGORY_SUFFIX,
            )
            const cutPathKey = generateKey(
                index,
                EDM_CALCULATOR_FORM_KEYS.CUT_PATH_SUFFIX,
            )
            const plateThicknessKey = generateKey(
                index,
                EDM_CALCULATOR_FORM_KEYS.PLATE_THICKNESS,
            )

            const {
                [gearKey]: gear,
                [roughnessKey]: roughness,
                [cutPathKey]: cutPath,
                [plateThicknessKey]: plateThickness,
            } = form.getFieldsValue([
                gearKey,
                roughnessKey,
                cutPathKey,
                plateThicknessKey,
            ])

            if (!gear || !roughness || !cutPath || !plateThickness) {
                return {
                    stepItems: null,
                    reason: 'Unable to calculate, please check for missing EDM values.',
                }
            }

            stepItem['wire'] = {
                gear,
                roughness,
                cutPath,
                plateThickness,
            }
        } else if (edmType === EDM_TYPES.SINKER) {
            const knownRemovalVolumeKey = generateKey(
                index,
                EDM_CALCULATOR_FORM_KEYS.KNOWN_REMOVAL_VOLUME_SUFFIX,
            )
            const roughnessKey = generateKey(
                index,
                EDM_CALCULATOR_FORM_KEYS.ROUGHNESS_CATEGORY_SUFFIX,
            )
            const setupsKey = generateKey(
                index,
                EDM_CALCULATOR_FORM_KEYS.SETUPS_SUFFIX,
            )
            const volumeRemovedKey = generateKey(
                index,
                EDM_CALCULATOR_FORM_KEYS.VOLUME_REMOVED_SUFFIX,
            )
            const desiredFilletRadiusKey = generateKey(
                index,
                EDM_CALCULATOR_FORM_KEYS.DESIRED_FILLET_RADIUS_SUFFIX,
            )
            const standardFilletRadiusKey = generateKey(
                index,
                EDM_CALCULATOR_FORM_KEYS.STANDARD_FILLET_RADIUS_SUFFIX,
            )
            const lengthOfFilletKey = generateKey(
                index,
                EDM_CALCULATOR_FORM_KEYS.LENGTH_OF_FILLET_SUFFIX,
            )

            const {
                [knownRemovalVolumeKey]: knownRemovalVolume,
                [roughnessKey]: roughness,
                [setupsKey]: setups,
            } = form.getFieldsValue([
                knownRemovalVolumeKey,
                roughnessKey,
                setupsKey,
            ])

            if (!knownRemovalVolume || !roughness || !setups) {
                return {
                    stepItems: null,
                    reason: 'Unable to calculate, please check for missing EDM values.',
                }
            }

            if (knownRemovalVolume === 'true') {
                const { [volumeRemovedKey]: volumeRemoved } =
                    form.getFieldsValue([volumeRemovedKey])

                if (!volumeRemovedKey) {
                    return {
                        stepItems: null,
                        reason: 'Unable to calculate, please check for missing EDM values.',
                    }
                }

                stepItem['sinkerKnownRemoval'] = {
                    roughness,
                    setups,
                    volumeRemoved,
                }
            } else {
                const {
                    [desiredFilletRadiusKey]: desiredFilletRadius,
                    [standardFilletRadiusKey]: standardFilletRadius,
                    [lengthOfFilletKey]: lengthOfFillet,
                } = form.getFieldsValue([
                    desiredFilletRadiusKey,
                    standardFilletRadiusKey,
                    lengthOfFilletKey,
                ])

                if (
                    !desiredFilletRadius ||
                    !standardFilletRadius ||
                    !lengthOfFillet
                ) {
                    return {
                        stepItems: null,
                        reason: 'Unable to calculate, please check for missing EDM values.',
                    }
                }

                stepItem['sinkerNoKnownRemoval'] = {
                    roughness,
                    setups,
                    desiredFilletRadius,
                    standardFilletRadius,
                    lengthOfFillet,
                }
            }
        }

        stepItemsFormValues.push(stepItem)
    }

    if (!stepItemsFormValues.length) {
        return { stepItems: null }
    }

    return { stepItems: stepItemsFormValues }
}

export const onCalculate = async ({
    qmrId,
    steps,
    form,
    client,
    setSavedStepItems,
}: {
    qmrId: string
    steps: number[]
    form: FormInstance
    client: ApolloClient<object>
    setSavedStepItems: (stepItems: StepItemType[] | null) => void
}) => {
    const { stepItems, reason } = generateStepItems(form, steps)

    if (!stepItems) {
        message.error(reason || 'Missing edm calculator information.')
        return
    }
    const edmCalculatorInput = { stepItems }
    const result = await client.query<
        EdmCalculatorPendingPriceQueryType,
        edmCalculatorPendingPriceQueryVariablesType
    >({
        query: edmCalculatorPendingPriceQuery,
        variables: {
            qmrId,
            edmCalculatorInput,
        },
        fetchPolicy: 'network-only',
    })

    if (
        result.data.getPendingEdmCalculatorDomesticAndOverseasPrice?.stepItems
    ) {
        const { stepItems: stepItemsWithPrices } =
            result.data.getPendingEdmCalculatorDomesticAndOverseasPrice

        setSavedStepItems(stepItemsWithPrices)

        let usaIsUnavailable = false
        const stepItemsTotal = stepItemsWithPrices.reduce(
            (prev, curr) => {
                if (curr?.priceInCents?.china) {
                    const chinaCentsToDollar = curr.priceInCents.china / 100

                    prev.china += chinaCentsToDollar
                }

                if (curr?.priceInCents?.india) {
                    const indiaCentsToDollar = curr.priceInCents.india / 100

                    prev.india += indiaCentsToDollar
                }

                if (!curr?.priceInCents?.usa && curr?.priceInCents?.usa !== 0) {
                    usaIsUnavailable = true
                    return prev
                }

                const usaCentsToDollar = curr?.priceInCents?.usa / 100

                prev.usa += usaCentsToDollar
                return prev
            },
            { usa: 0, china: 0, india: 0 },
        )

        const totalFixed = {
            usa: stepItemsTotal.usa.toFixed(2),
            china: stepItemsTotal.china.toFixed(2),
            india: stepItemsTotal.india.toFixed(2),
            usaIsUnavailable,
        }

        form.setFieldsValue({
            ['edmProductionCosts']: usaIsUnavailable ? null : totalFixed.usa,
        })

        form.setFieldsValue({
            ['chinaEdmProductionCosts']: totalFixed.china,
        })

        form.setFieldsValue({
            ['indiaEdmProductionCosts']: totalFixed.india,
        })
    }
}
